import { useAppSelector } from "common/hooks";
import { motion } from "framer-motion";
import { Apple } from "lucide-react";
import { useGetSubscriptionStatus } from "modules/workspaces/api";
import { FunctionComponent, PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";

export const LayoutController: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const auth = user !== null;
  const { data, isLoading } = useGetSubscriptionStatus();
  const location = useLocation();

  if (auth && user?.role !== "admin") {
    return (
      <div className="from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 relative flex min-h-[50vh] w-full items-center justify-center overflow-hidden bg-gradient-to-br px-4 py-20">
        {/* Decorative elements */}
        <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-md text-center"
        >
          <h2 className="text-transparent mb-6 bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-4xl font-bold tracking-tight sm:text-5xl">
            Accédez à l'application
          </h2>

          <p className="text-slate-600 dark:text-slate-300 mb-8">
            Téléchargez notre application sur l'App Store et profitez d'une
            expérience optimale sur votre appareil iOS.
          </p>

          <motion.a
            href="https://apps.apple.com/fr/app/féefroid/id6744965100?l=en-GB"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-blue-600 to-blue-400 p-0.5 text-white"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            aria-label="Télécharger sur l'App Store"
          >
            <span className="dark:bg-slate-900 relative flex items-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-medium text-blue-600 transition-all duration-300 ease-out group-hover:bg-opacity-0 group-hover:text-white dark:text-blue-400 dark:group-hover:bg-opacity-0 dark:group-hover:text-white">
              <Apple className="h-5 w-5" />
              Télécharger sur l'App Store
            </span>
          </motion.a>

          {/* Device mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12 flex justify-center"
          >
            <div className="border-slate-800 bg-slate-800 dark:border-slate-700 relative h-[200px] w-[100px] rounded-[24px] border-4 p-1 shadow-lg">
              <div className="bg-slate-700 absolute left-1/2 top-2 h-2 w-16 -translate-x-1/2 rounded-full"></div>
              <div className="dark:bg-slate-800 h-full w-full overflow-hidden rounded-[18px] bg-blue-50"></div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (
    !isLoading &&
    !data?.data.active &&
    auth &&
    !location.pathname.includes("/auth/subscription")
  ) {
    return <Navigate to="/auth/subscription" replace={true} />;
  }

  if (
    !isLoading &&
    data?.data.active &&
    auth &&
    location.pathname.startsWith("/auth")
  ) {
    return <Navigate to="/" replace={true} />;
  }

  if (!auth && !location.pathname.startsWith("/auth")) {
    return <Navigate to="/auth/sign-in" replace={true} />;
  }

  return <>{children}</>;
};
